import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import './MainLayout.css';
import { clearTokens, checkToken } from '../services/authService';
import websocketService from '../services/websocketService';

function MainLayout({ onLogout }) {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile only
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false); // Desktop only

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const toggleCollapse = () => {
    setIsSidebarCollapsed(prev => !prev);
  };

  // Window resize handler - close sidebar on desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ESC key handler - close sidebar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isSidebarOpen) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSidebarOpen]);

  // Scroll lock - prevent background scrolling when sidebar is open on mobile
  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isSidebarOpen]);

  const handleLogout = () => {
    // 执行登出逻辑
    if (onLogout) {
      onLogout();
    } else {
        // Fallback if not provided
        clearTokens();
        websocketService.disconnect();
        navigate('/login');
    }
  };

  const handleCheckToken = async () => {
    try {
      await checkToken();
      alert('Token 检查成功 (或已自动刷新)');
    } catch (error) {
      console.error('Check token failed:', error);
      alert(`检查失败: ${error.message}，即将退出登录`);
      handleLogout();
    }
  };

  return (
    <div className="main-layout">
      {/* Hamburger button for mobile */}
      <button
        className="hamburger-btn"
        onClick={toggleSidebar}
        aria-label="Toggle navigation menu"
        aria-expanded={isSidebarOpen}
      >
        <span className="hamburger-icon">☰</span>
      </button>

      {/* Expand button for desktop (when sidebar is collapsed) */}
      {isSidebarCollapsed && (
        <button
          className="expand-btn"
          onClick={toggleCollapse}
          aria-label="Expand sidebar"
          title="展开侧边栏"
        >
          <span className="expand-icon">▶</span>
        </button>
      )}

      {/* Backdrop overlay for mobile */}
      <div
        className={`sidebar-backdrop ${isSidebarOpen ? 'active' : ''}`}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <aside className={`sidebar ${isSidebarOpen ? 'open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-header">
          <div
            className="sidebar-header-content"
            onClick={() => navigate('/')}
            title="返回主页"
          >
            <span className="sidebar-header-icon">🏠</span>
            <h2>Portal</h2>
          </div>
          {/* Collapse button for desktop */}
          <button
            className="collapse-btn"
            onClick={toggleCollapse}
            aria-label="Collapse sidebar"
            title="折叠侧边栏"
          >
            <span className="collapse-icon">◀</span>
          </button>
        </div>
        <nav className="sidebar-nav">
          <NavLink
            to="/create-user"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            onClick={closeSidebar}
          >
            👥 创建用户
          </NavLink>
          <NavLink
            to="/web-chat"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            onClick={closeSidebar}
          >
            💬 聊天
          </NavLink>
          <NavLink
            to="/guess-avatar"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            onClick={closeSidebar}
          >
            🎮 猜头像
          </NavLink>
        </nav>
        <div className="sidebar-footer">
          <button onClick={handleCheckToken} className="btn-refresh-token" title="检查并刷新Token">
            🔄 刷新状态
          </button>
          <button onClick={handleLogout} className="btn-logout">
            退出登录
          </button>
        </div>
      </aside>
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}

export default MainLayout;
